import { forwardRef, type SelectHTMLAttributes } from 'react';
import { cn } from '../../lib/cn';

export interface SelectOption<T extends string = string> {
  value: T;
  label: string;
}

interface Props<T extends string = string>
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'value' | 'onChange'> {
  label?: string;
  hint?: string;
  error?: string;
  options: ReadonlyArray<SelectOption<T>>;
  value?: T;
  onChange?: (value: T) => void;
  placeholder?: string;
}

export const Select = forwardRef<HTMLSelectElement, Props>(function Select(
  { label, hint, error, options, value, onChange, placeholder, className, id, ...rest },
  ref,
) {
  const selectId = id ?? `s-${Math.random().toString(36).slice(2, 9)}`;
  return (
    <div className="space-y-1.5">
      {label && (
        <label
          htmlFor={selectId}
          className="block text-[11px] font-semibold text-ink-muted uppercase tracking-wider"
        >
          {label}
        </label>
      )}
      <select
        {...rest}
        ref={ref}
        id={selectId}
        value={value ?? ''}
        onChange={(e) => onChange?.(e.target.value)}
        className={cn(
          'w-full h-10 px-3 pr-9 rounded-lg appearance-none cursor-pointer',
          'bg-surface-elevated border border-surface-border text-ink',
          'focus:outline-none focus:ring-2 focus:ring-brand-500/40 focus:border-brand-500',
          'transition-colors duration-150',
          'bg-no-repeat bg-[length:18px] bg-[position:right_8px_center]',
          // Chevron como SVG inline (compatible con ambos modos via currentColor)
          "bg-[image:url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='gray' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\")]",
          error && 'border-loss focus:ring-loss/40 focus:border-loss',
          className,
        )}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {error ? (
        <p className="text-xs text-loss">{error}</p>
      ) : hint ? (
        <p className="text-xs text-ink-subtle">{hint}</p>
      ) : null}
    </div>
  );
});
